import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState, Chip, DoctorLabStatus } from "../components/Common";
import { FlaskConical, ClipboardList, CheckCircle2, AlertTriangle, Zap, Search, X, Download, Calendar, Paperclip, FileText } from "lucide-react";
import { API_BASE, getToken } from "../lib/api";
import { toast } from "sonner";

export default function Lab() {
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);
  const [results, setResults] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [panels, setPanels] = useState([]);
  const [tab, setTab] = useState("worklist");
  const [filter, setFilter] = useState("all"); // all | waiting | running | ready-today | critical
  const [expanded, setExpanded] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  const load = async () => {
    const [{data:o},{data:p}] = await Promise.all([api.get("/lab/orders"), api.get("/lab/panels")]);
    setOrders(o || []); setPanels(p || []);
  };
  useEffect(() => { load(); }, []);

  const waitingOrders = orders.filter((o) => o.status === "Ordered");
  const runningOrders = orders.filter((o) => ["Collected","In Progress","Awaiting Verification"].includes(o.status));
  const pending = [...waitingOrders, ...runningOrders];
  const verified = orders.filter((o) => o.status === "Verified");
  const readyToday = verified.filter((o) => (o.verified_at || "").slice(0, 10) === today);
  const critical = verified.filter((o) => (o.results || []).some((r) => r.flag === "critical"));
  const inRange = (o) => {
    if (!o.verified_at) return false;
    const d = new Date(o.verified_at).toISOString().slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };
  const completedFiltered = verified.filter(inRange);

  // KPI-driven filters: pick the exact same set the KPI counts
  let rowsForTab;
  if (tab === "worklist") {
    if (filter === "waiting")      rowsForTab = waitingOrders;
    else if (filter === "running") rowsForTab = runningOrders;
    else                            rowsForTab = pending;
  } else {
    if (filter === "critical")         rowsForTab = critical;              // all-time, bypasses date range
    else if (filter === "ready-today") rowsForTab = readyToday;            // today only, bypasses date range
    else                                rowsForTab = completedFiltered;    // honours the date range widget
  }

  const filterLabels = {
    waiting: `Waiting · not started yet · ${waitingOrders.length}`,
    running: `Running · being processed · ${runningOrders.length}`,
    "ready-today": `Ready today · released to doctor · ${readyToday.length}`,
    critical: `Critical results · ${critical.length}`,
  };

  const clearFilter = () => setFilter("all");
  const switchTab = (nextTab) => { setTab(nextTab); setFilter("all"); };

  const applyKpi = (kind) => {
    if (kind === "waiting")       { setTab("worklist"); setFilter("waiting"); }
    else if (kind === "running")  { setTab("worklist"); setFilter("running"); }
    else if (kind === "ready-today") { setTab("completed"); setFilter("ready-today"); }
    else if (kind === "critical") { setTab("completed"); setFilter("critical"); }
  };

  const exportCompletedCSV = () => {
    const header = ["Order","Patient","Patient ID","Tests","Priority","Status","Released","Critical"];
    const lines = [header.join(",")];
    completedFiltered.forEach((o) => {
      const anyCritical = (o.results || []).some((r) => r.flag === "critical") ? "Yes" : "No";
      const row = [
        o.order_no,
        `"${(o.patient_name || "").replace(/"/g,'""')}"`,
        o.patient_ths_id || "",
        `"${(o.tests || []).join("; ")}"`,
        o.priority || "",
        "Ready",
        o.verified_at ? new Date(o.verified_at).toISOString() : "",
        anyCritical,
      ];
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `completed-lab-results_${dateFrom}_to_${dateTo}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`Exported ${completedFiltered.length} result${completedFiltered.length===1?"":"s"}`);
  };
  const setRangePreset = (preset) => {
    const d = new Date();
    const iso = (x) => x.toISOString().slice(0,10);
    if (preset === "today") { setDateFrom(iso(d)); setDateTo(iso(d)); }
    else if (preset === "7d") { const s = new Date(d); s.setDate(d.getDate()-6); setDateFrom(iso(s)); setDateTo(iso(d)); }
    else if (preset === "30d") { const s = new Date(d); s.setDate(d.getDate()-29); setDateFrom(iso(s)); setDateTo(iso(d)); }
    else if (preset === "all") { setDateFrom(""); setDateTo(""); }
  };

  const collect = async (order) => {
    try { await api.post(`/lab/collect/${order.order_id}`); toast.success("Test set to Running · barcode generated"); load(); }
    catch { toast.error("Failed"); }
  };

  const openEntry = (order) => {
    setActive(order);
    const existing = Object.fromEntries((order.attachments || []).map((a) => [a.test, { filename: a.filename }]));
    setResults((order.tests || []).map((t) => ({
      test: t,
      value: "",
      unit: "",
      reference: "",
      flag: "normal",
      attachment: existing[t] || null,
    })));
  };

  const uploadAttachment = async (i, file) => {
    if (!active || !file) return;
    if (file.type && file.type !== "application/pdf") { toast.error("PDF only"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF exceeds 15 MB"); return; }
    const test = results[i].test;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/lab/orders/${active.order_id}/attachment?test=${encodeURIComponent(test)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = [...results]; c[i].attachment = { filename: data.filename }; setResults(c);
      toast.success(`Attached ${data.filename}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  const removeAttachment = async (i) => {
    if (!active) return;
    const test = results[i].test;
    try {
      await api.delete(`/lab/orders/${active.order_id}/attachment?test=${encodeURIComponent(test)}`);
      const c = [...results]; c[i].attachment = null; setResults(c);
      toast.success("Attachment removed");
    } catch { toast.error("Remove failed"); }
  };

  const viewAttachment = async (orderId, test) => {
    try {
      const res = await api.get(`/lab/orders/${orderId}/attachment?test=${encodeURIComponent(test)}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open report"); }
  };

  const saveResults = async (statusFinal) => {
    if (!active) return;
    if (statusFinal === "Verified") {
      const missing = results.filter((r) => !String(r.value).trim()).map((r) => r.test);
      if (missing.length) {
        toast.error(`Enter a structured Value for: ${missing.join(", ")}. PDFs alone don't trigger alerts.`);
        return;
      }
    }
    try {
      const stripped = results.map(({ attachment, ...rest }) => rest); // don't echo attachment; backend re-attaches from order doc
      await api.post("/lab/results", { order_id: active.order_id, results: stripped, status: statusFinal });
      toast.success(statusFinal === "Verified" ? "Marked as Ready · released to doctor" : "Result draft saved");
      setActive(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader title="Laboratory" subtitle="Collect specimens, enter results, verify and release." actions={
        <button data-testid="apply-panel-btn" onClick={() => setShowPanel(true)} className="h-10 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#1565C0]"><Zap className="w-4 h-4" /> Apply test panel</button>
      } />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="lab-kpi-waiting"  onClick={() => applyKpi("waiting")}      label="Waiting"     value={waitingOrders.length} icon={ClipboardList} tone="amber" hint="Not started yet" />
        <KpiCard testId="lab-kpi-running"  onClick={() => applyKpi("running")}      label="Running"     value={runningOrders.length} icon={FlaskConical}  tone="blue"  hint="Being processed" />
        <KpiCard testId="lab-kpi-ready"    onClick={() => applyKpi("ready-today")}  label="Ready today" value={readyToday.length}    icon={CheckCircle2}  tone="green" hint="Released to doctor" />
        <KpiCard testId="lab-kpi-critical" onClick={() => applyKpi("critical")}     label="Critical"    value={critical.length}      icon={AlertTriangle} tone="red"   hint="Flagged results" />
      </div>

      {filter !== "all" && (
        <div className="rounded-lg border border-[#1976D2]/30 bg-[#f0f6ff] px-4 py-2.5 flex items-center justify-between" data-testid="lab-filter-banner">
          <div className="text-sm text-[#0d3e75]">
            Showing <span className="font-semibold">{filterLabels[filter]}</span>
          </div>
          <button data-testid="lab-clear-filter" onClick={clearFilter} className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs font-medium flex items-center gap-1">
            <X className="w-3 h-3" /> Show all
          </button>
        </div>
      )}

      <SectionCard
        title={
          <div className="flex items-center gap-1" data-testid="lab-tabs">
            <button
              data-testid="lab-tab-worklist"
              onClick={() => switchTab("worklist")}
              className={`px-3 h-8 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${tab === "worklist" ? "bg-[#1976D2] text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Worklist <span className={`text-[11px] tabular ${tab === "worklist" ? "text-white/80" : "text-slate-400"}`}>{pending.length}</span>
            </button>
            <button
              data-testid="lab-tab-completed"
              onClick={() => switchTab("completed")}
              className={`px-3 h-8 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${tab === "completed" ? "bg-[#2E7D32] text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Completed test results <span className={`text-[11px] tabular ${tab === "completed" ? "text-white/80" : "text-slate-400"}`}>{verified.length}</span>
            </button>
          </div>
        }
        actions={
          tab === "completed" ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="completed-toolbar">
              <div className="hidden lg:flex items-center gap-1 border border-slate-200 rounded-lg p-0.5">
                {[["today","Today"],["7d","7 days"],["30d","30 days"],["all","All"]].map(([k,l])=>(
                  <button key={k} data-testid={`lab-preset-${k}`} onClick={()=>setRangePreset(k)} className="px-2 h-7 text-xs font-medium rounded-md text-slate-600 hover:bg-slate-100">{l}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <input data-testid="lab-date-from" type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} className="h-8 px-2 rounded-lg border border-slate-200 text-xs" />
                <span className="text-xs text-slate-400">to</span>
                <input data-testid="lab-date-to" type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} className="h-8 px-2 rounded-lg border border-slate-200 text-xs" />
              </div>
              <button data-testid="lab-export-csv" onClick={exportCompletedCSV} disabled={completedFiltered.length===0} className="h-8 px-3 rounded-lg bg-[#2E7D32] text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-[#1B5E20] disabled:opacity-50">
                <Download className="w-3.5 h-3.5" /> Export CSV ({completedFiltered.length})
              </button>
            </div>
          ) : (
            <span className="text-xs text-slate-500 tabular" data-testid="worklist-count">{rowsForTab.length} showing</span>
          )
        }
      >
        {rowsForTab.length === 0 ? (
          <EmptyState icon={FlaskConical} title={tab === "worklist" ? "Nothing to run right now" : "No completed results in this range"} description={tab === "worklist" ? "New orders from doctors land here as Waiting." : "Change the date range or clear the filter to see older results. New results move here automatically the moment you tap Update to Ready."} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Order</th><th className="text-left px-4 py-2.5">Patient</th>
                  <th className="text-left px-4 py-2.5">Tests</th><th className="text-left px-4 py-2.5">Priority</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  {tab === "completed" && <th className="text-left px-4 py-2.5">Released</th>}
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rowsForTab.map((o) => (
                  <React.Fragment key={o.order_id}>
                  <tr data-testid={`lab-order-${o.order_id}`}>
                    <td className="px-4 py-2.5 tabular">{o.order_no}</td>
                    <td className="px-4 py-2.5">{o.patient_name}<div className="text-xs text-slate-500">{o.patient_ths_id}</div></td>
                    <td className="px-4 py-2.5">{(o.tests || []).join(", ")}</td>
                    <td className="px-4 py-2.5"><StatusPill status={o.priority === "STAT" ? "critical" : (o.priority === "Urgent" ? "warning" : "normal")} /></td>
                    <td className="px-4 py-2.5"><DoctorLabStatus status={o.status} /></td>
                    {tab === "completed" && (
                      <td className="px-4 py-2.5 text-xs text-slate-500 tabular">
                        {o.verified_at ? new Date(o.verified_at).toLocaleString(undefined,{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "—"}
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-right">
                      {tab === "worklist" && o.status === "Ordered" && <button onClick={() => collect(o)} data-testid={`lab-start-${o.order_id}`} className="text-xs text-[#1565C0] font-medium hover:underline">Start running</button>}
                      {tab === "worklist" && ["Collected","In Progress","Awaiting Verification"].includes(o.status) && <button onClick={() => openEntry(o)} data-testid={`lab-enter-${o.order_id}`} className="text-xs text-[#1565C0] font-medium hover:underline">Enter results</button>}
                      {tab === "completed" && (
                        <button onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)} data-testid={`lab-view-${o.order_id}`} className="text-xs text-[#1565C0] font-medium hover:underline">
                          {expanded === o.order_id ? "Hide" : "View results"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {tab === "completed" && expanded === o.order_id && (o.results || []).length > 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-3 bg-slate-50">
                        <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
                          <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-500">
                            <tr><th className="text-left px-3 py-1.5">Test</th><th className="text-left px-3 py-1.5">Value</th><th className="text-left px-3 py-1.5">Unit</th><th className="text-left px-3 py-1.5">Reference</th><th className="text-left px-3 py-1.5">Flag</th><th className="text-left px-3 py-1.5">Attachment</th></tr>
                          </thead>
                          <tbody>
                            {o.results.map((r, idx) => (
                              <tr key={idx} className="border-t border-slate-100">
                                <td className="px-3 py-1.5">{r.test}</td>
                                <td className="px-3 py-1.5 tabular font-semibold">{r.value}</td>
                                <td className="px-3 py-1.5 tabular">{r.unit}</td>
                                <td className="px-3 py-1.5 text-slate-500 tabular">{r.reference}</td>
                                <td className="px-3 py-1.5"><StatusPill status={r.flag} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {active && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full p-6 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="lab-entry-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-500">Enter results — {active.order_no}</div>
                <div className="font-semibold text-slate-900">{active.patient_name} · {active.patient_ths_id}</div>
              </div>
              <button onClick={() => setActive(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="mt-4 space-y-3 max-h-96 overflow-y-auto pr-1">
              {results.map((r, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`entry-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-3 text-sm font-medium text-slate-800 self-center">{r.test}</div>
                    <input placeholder="Value" data-testid={`lab-value-${i}`} value={r.value} onChange={(e) => { const c = [...results]; c[i].value = e.target.value; setResults(c); }} className="col-span-3 h-9 px-2 rounded-lg border border-slate-200 text-sm tabular" />
                    <input placeholder="Unit" value={r.unit} onChange={(e) => { const c = [...results]; c[i].unit = e.target.value; setResults(c); }} className="col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                    <input placeholder="Reference" value={r.reference} onChange={(e) => { const c = [...results]; c[i].reference = e.target.value; setResults(c); }} className="col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                    <select value={r.flag} onChange={(e) => { const c = [...results]; c[i].flag = e.target.value; setResults(c); }} className="col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
                      <option value="normal">Normal</option><option value="warning">Abnormal</option><option value="critical">Critical</option>
                    </select>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    {r.attachment ? (
                      <>
                        <span className="chip chip-blue flex items-center gap-1"><FileText className="w-3 h-3" /> {r.attachment.filename}</span>
                        <button type="button" onClick={() => viewAttachment(active.order_id, r.test)} data-testid={`lab-view-att-${i}`} className="text-[11px] text-[#1565C0] font-medium hover:underline">View</button>
                        <button type="button" onClick={() => removeAttachment(i)} data-testid={`lab-remove-att-${i}`} className="text-[11px] text-[#B71C1C] font-medium hover:underline">Remove</button>
                        <span className="text-[11px] text-slate-500 ml-auto">Supplementary — enter the Value above so critical alerts still fire</span>
                      </>
                    ) : (
                      <label className="text-[11px] text-[#1565C0] font-medium hover:underline cursor-pointer flex items-center gap-1" data-testid={`lab-attach-label-${i}`}>
                        <Paperclip className="w-3 h-3" /> Attach report (PDF) · optional
                        <input type="file" accept="application/pdf" className="hidden" data-testid={`lab-attach-input-${i}`} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(i, f); e.target.value = ""; }} />
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => saveResults("Awaiting Verification")} data-testid="lab-save-draft" className="px-4 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Save draft</button>
              <button onClick={() => saveResults("Verified")} data-testid="lab-mark-ready" className="px-4 h-9 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold">Update to Ready</button>
            </div>
          </div>
        </div>
      )}
      {showPanel && <ApplyPanelModal panels={panels} onClose={() => setShowPanel(false)} onDone={() => { setShowPanel(false); load(); }} />}
    </div>
  );
}

function ApplyPanelModal({ panels, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [code, setCode] = useState(panels[0]?.code || "");
  const [priority, setPriority] = useState("Routine");
  useEffect(() => { if (q.length < 2) { setResults([]); return; } api.get(`/patients?q=${encodeURIComponent(q)}&limit=8`).then((r) => setResults(r.data || [])); }, [q]);
  const submit = async () => {
    if (!patient) return toast.error("Select a patient");
    try {
      await api.post("/lab/panels/apply", { patient_id: patient.patient_id, code, priority });
      const panel = panels.find((p) => p.code === code);
      toast.success(`${panel?.name || "Panel"} ordered · auto-billed`);
      onDone();
    } catch { toast.error("Failed to apply panel"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="apply-panel-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Apply lab test panel</div>
          <button onClick={onClose} className="text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        {!patient ? (
          <div>
            <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="panel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient…" className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="mt-2 divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {results.map((p) => (
                <button key={p.patient_id} onClick={() => setPatient(p)} className="w-full text-left p-2 hover:bg-slate-50 rounded" data-testid={`panel-pick-${p.patient_id}`}>
                  <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
                  <div className="text-xs text-slate-500 tabular">{p.ths_id}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between mb-4">
              <div><div className="font-medium text-sm">{patient.first_name} {patient.last_name}</div><div className="text-xs text-slate-500 tabular">{patient.ths_id}</div></div>
              <button onClick={() => setPatient(null)} className="text-xs text-slate-500 hover:text-slate-800">Change</button>
            </div>
            <label className="block mb-3"><span className="text-[11px] uppercase text-slate-500 font-semibold">Panel</span>
              <select data-testid="panel-select" value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                {panels.map((p) => <option key={p.code} value={p.code}>{p.name} — {p.tests.length} tests</option>)}
              </select>
              {code && <div className="mt-2 text-xs text-slate-500">Includes: {panels.find((p) => p.code === code)?.tests.join(", ")}</div>}
            </label>
            <label className="block mb-4"><span className="text-[11px] uppercase text-slate-500 font-semibold">Priority</span>
              <select data-testid="panel-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option>Routine</option><option>Urgent</option><option>STAT</option>
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
              <button data-testid="panel-submit" onClick={submit} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Order panel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
